import { Millisecond } from "@pagopa/ts-commons/lib/units";
import { DeferredPromise } from "@pagopa/ts-commons/lib/promises";
import { createClient as creatWalletClient } from "../../../generated/definitions/checkout-wallets-v1/client";
import { createClient as createEcommerceClient } from "../../../generated/definitions/payment-ecommerce/client";
import { createClient as createEcommerceClientV2 } from "../../../generated/definitions/payment-ecommerce-v2/client";
import { createClient as createEcommerceAuthClientV1 } from "../../../generated/definitions/payment-ecommerce-auth-v1/client";
import { createClient as createAuthServiceClient } from "../../../generated/definitions/checkout-auth-service-v1/client";
import { createClient as createCheckoutFeatureFlagsClient } from "../../../generated/definitions/checkout-feature-flags/client";

import { getConfigOrThrow } from "../config/config";
import {
  constantPollingWithPromisePredicateFetch,
  retryingFetch,
} from "../config/fetch";

const conf = getConfigOrThrow();
const retries: number = conf.CHECKOUT_API_CLIENT_RETRY_NUMBERS;
const delay: number = conf.CHECKOUT_API_CLIENT_RETRY_DELAY;

/**
 * Api client for payment ecommerce API V1
 */
export const apiWalletEcommerceClient = creatWalletClient({
  baseUrl: conf.CHECKOUT_PAGOPA_APIM_HOST,
  basePath: conf.CHECKOUT_API_WALLET_BASEPATH_V1 as string,
  fetchApi: retryingFetch(fetch, conf.CHECKOUT_API_TIMEOUT as Millisecond, 3),
});

/**
 * Api client for payment ecommerce API V1
 */
export const apiPaymentEcommerceClient = createEcommerceClient({
  baseUrl: conf.CHECKOUT_PAGOPA_APIM_HOST,
  basePath: conf.CHECKOUT_API_ECOMMERCE_BASEPATH as string,
  fetchApi: retryingFetch(fetch, conf.CHECKOUT_API_TIMEOUT as Millisecond, 3),
});

/**
 * Api client for payment ecommerce API V2
 */
export const apiPaymentEcommerceClientV2 = createEcommerceClientV2({
  baseUrl: conf.CHECKOUT_PAGOPA_APIM_HOST,
  basePath: conf.CHECKOUT_API_ECOMMERCE_BASEPATH_V2 as string,
  fetchApi: retryingFetch(fetch, conf.CHECKOUT_API_TIMEOUT as Millisecond, 3),
});

/**
 * Api client for payment ecommerce API V3 (authenticated client)
 */
/* export const apiPaymentEcommerceClientV3 = createEcommerceClientV3({
  baseUrl: conf.CHECKOUT_PAGOPA_APIM_HOST,
  basePath: conf.CHECKOUT_API_ECOMMERCE_BASEPATH_V3 as string,
  fetchApi: retryingFetch(fetch, conf.CHECKOUT_API_TIMEOUT as Millisecond, 3),
}); */

/**
 * Api client for payment ecommerce API V4
 */
/* export const apiPaymentEcommerceClientV4 = createEcommerceClientV4({
  baseUrl: conf.CHECKOUT_PAGOPA_APIM_HOST,
  basePath: conf.CHECKOUT_API_ECOMMERCE_BASEPATH_V4 as string,
  fetchApi: retryingFetch(fetch, conf.CHECKOUT_API_TIMEOUT as Millisecond, 3),
}); */

/**
 * Api client for payment ecommerce authenticated API V1 (authenticated client)
 */
export const apiPaymentEcommerceAuthClientV1 = createEcommerceAuthClientV1({
  baseUrl: conf.CHECKOUT_PAGOPA_APIM_HOST,
  basePath: conf.CHECKOUT_API_ECOMMERCE_BASEPATH_AUTH_V1 as string,
  fetchApi: retryingFetch(fetch, conf.CHECKOUT_API_TIMEOUT as Millisecond, 3),
});

/**
 * Api client for checkout feature flags
 */
export const apiCheckoutFeatureFlags = createCheckoutFeatureFlagsClient({
  baseUrl: conf.CHECKOUT_PAGOPA_APIM_HOST,
  basePath: conf.CHECKOUT_API_FEATURE_FLAGS_BASEPATH as string,
  fetchApi: retryingFetch(fetch, conf.CHECKOUT_API_TIMEOUT as Millisecond, 3),
});

/**
 * Api client for ecommerce API calculate fee with retry execution
 */
export const apiPaymentEcommerceClientWithRetry = createEcommerceClient({
  baseUrl: conf.CHECKOUT_PAGOPA_APIM_HOST,
  basePath: conf.CHECKOUT_API_ECOMMERCE_BASEPATH as string,
  fetchApi: constantPollingWithPromisePredicateFetch(
    DeferredPromise<boolean>().e1,
    retries,
    delay,
    conf.CHECKOUT_API_TIMEOUT as Millisecond,
    async (r: Response): Promise<boolean> => r.status > 499
  ),
});

/**
 * Api client for ecommerce API calculate fee with retry execution
 */
export const apiPaymentEcommerceClientWithRetryV2 = createEcommerceClientV2({
  baseUrl: conf.CHECKOUT_PAGOPA_APIM_HOST,
  basePath: conf.CHECKOUT_API_ECOMMERCE_BASEPATH_V2 as string,
  fetchApi: constantPollingWithPromisePredicateFetch(
    DeferredPromise<boolean>().e1,
    retries,
    delay,
    conf.CHECKOUT_API_TIMEOUT as Millisecond,
    async (r: Response): Promise<boolean> => r.status > 499
  ),
});

/**
 * Api client for ecommerce API calculate fee with retry execution
 */
/* export const apiPaymentEcommerceClientWithRetryV3 = createEcommerceClientV3({
  baseUrl: conf.CHECKOUT_PAGOPA_APIM_HOST,
  basePath: conf.CHECKOUT_API_ECOMMERCE_BASEPATH_V3 as string,
  fetchApi: constantPollingWithPromisePredicateFetch(
    DeferredPromise<boolean>().e1,
    retries,
    delay,
    conf.CHECKOUT_API_TIMEOUT as Millisecond,
    async (r: Response): Promise<boolean> => r.status > 499
  ),
}); */

/**
 * Api client for ecommerce API calculate fee with retry execution
 */
export const apiPaymentEcommerceAuthClientWithRetryV1 =
  createEcommerceAuthClientV1({
    baseUrl: conf.CHECKOUT_PAGOPA_APIM_HOST,
    basePath: conf.CHECKOUT_API_ECOMMERCE_BASEPATH_AUTH_V1 as string,
    fetchApi: constantPollingWithPromisePredicateFetch(
      DeferredPromise<boolean>().e1,
      retries,
      delay,
      conf.CHECKOUT_API_TIMEOUT as Millisecond,
      async (r: Response): Promise<boolean> => r.status > 499
    ),
  });
/**
 * Api client for checkout auth service API V1
 */
export const apiCheckoutAuthServiceClientV1 = createAuthServiceClient({
  baseUrl: conf.CHECKOUT_PAGOPA_APIM_HOST,
  basePath: conf.CHECKOUT_API_AUTH_SERVICE_BASEPATH_V1 as string,
  fetchApi: retryingFetch(fetch, conf.CHECKOUT_API_TIMEOUT as Millisecond, 3),
});

export const apiCheckoutAuthServiceClientAuthTokenV1 = createAuthServiceClient({
  baseUrl: conf.CHECKOUT_PAGOPA_APIM_HOST,
  basePath: conf.CHECKOUT_API_AUTH_SERVICE_BASEPATH_V1 as string,
  fetchApi: retryingFetch(
    fetch,
    conf.CHECKOUT_API_TIMEOUT as Millisecond,
    2,
    (response: Response) =>
      response.status === 503 ||
      response.status === 504 ||
      response.status === 429
  ),
});

/**
 * Api client for checkout auth service API V1 with retry with 5xx error status
 */
export const apiCheckoutAuthServiceWithRetryV1 = createAuthServiceClient({
  baseUrl: conf.CHECKOUT_PAGOPA_APIM_HOST,
  basePath: conf.CHECKOUT_API_AUTH_SERVICE_BASEPATH_V1 as string,
  fetchApi: retryingFetch(
    fetch,
    conf.CHECKOUT_API_TIMEOUT as Millisecond,
    3,
    (response: Response) => response.status >= 500
  ),
});
